/**
 * Upload context keys sent to the API (`key` in multipart POST /api/uploads).
 *
 * A key is all the browser ever sends. The S3 folder, file-count cap, size cap
 * and accepted content types all live server-side, in the `platform_constants`
 * rows of scope `upload` (with api-server's `uploads.catalog.fallback.ts` as the
 * boot-time fallback). Read the live values from `GET /api/uploads/catalog` via
 * `useUploadCatalogQuery` rather than hard-coding limits here.
 *
 * The list below must stay a subset of api-server's `UPLOAD_FOLDERS` — an
 * unknown key is rejected by the request schema.
 */
export const UploadContextKey = {
  DesignRequestInspiration: "design-request-inspiration",
  DesignRequestProof: "design-request-proof",
  OrderDesign: "order-design",
  OrderItemDst: "order-item-dst",
  Avatar: "avatar",
  /**
   * Catalog imagery. Basic and exclusive products use different keys because
   * they store into different S3 folders — exclusives share the prefix the
   * garments importer already writes to, so old and new exclusives sit
   * together. That is why the create form locks the product type as soon as
   * the first upload starts: a file cannot change folder afterwards.
   */
  ProductImage: "product-image",
  ExclusiveImage: "exclusive-image",
  ExclusiveSizeChart: "exclusive-size-chart",
  ExclusiveHowToMeasure: "exclusive-how-to-measure",
  /**
   * Files attached to a payment-record note. Its own bucket rather than the
   * notes one: there `uploads.relevant_id` means `notes.id`, and that table's
   * ids collide with `payment_activity_log`'s.
   */
  PaymentNoteAttachment: "payment-note-attachment",
  /** Files on a Notes-module note (`uploads.relevant_id` = `notes.id`), e.g. printer-order notes. */
  NoteAttachment: "note-attachment",
  /** Custom names and numbers CSV. Claimed onto `orders.id` when a regular order is placed. */
  CustomNamesCsv: "custom-names-csv",
} as const;

export type UploadContextKey =
  (typeof UploadContextKey)[keyof typeof UploadContextKey];

export type UploadCatalogItem = {
  key: UploadContextKey;
  folder: string;
  label: string;
  maxFiles: number;
  maxBytesPerFile: number;
  allowedContentTypes: string[];
};

export type CompletedUpload = {
  objectKey: string;
  cdnUrl: string;
  contentType: string;
  sizeBytes: number;
  fileName: string;
};

export type UploadedAsset = CompletedUpload;
